<template>
  <div class="changelog-container">
    <div class="changelog-toolbar">
      <a-radio-group v-model:value="group" button-style="solid" size="small">
        <a-radio-button
          v-for="option in groups"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </a-radio-button>
      </a-radio-group>
      <a-button size="small" :loading="isFetchingTaskHistory" @click="refresh">
        <template #icon><reload-outlined /></template>
        Refresh
      </a-button>
    </div>

    <a-skeleton v-if="isFetchingTaskHistory && !taskChangelog.length" active />

    <a-empty
      v-else-if="!visibleEntries.length"
      class="my-4"
      :description="
        group === 'all'
          ? 'No changes recorded for this operation yet.'
          : 'No changes in this section among the loaded entries.'
      "
    />

    <a-timeline v-else class="changelog-timeline">
      <a-timeline-item
        v-for="entry in visibleEntries"
        :key="entry.history_id"
        :color="timelineColor(entry.type)"
      >
        <changelog-entry
          :entry="entry"
          :group="group"
          :is-latest="entry.history_id === latestHistoryId"
          @restore="confirmRestore"
        />
      </a-timeline-item>
    </a-timeline>

    <div v-if="taskChangelogCursor" class="text-center">
      <a-button :loading="isFetchingTaskHistory" @click="loadMore">
        Load older changes
      </a-button>
    </div>
  </div>
</template>

<script>
import { h } from 'vue';
import { ReloadOutlined } from '@ant-design/icons-vue';
import { Modal } from 'ant-design-vue';
import { mapActions, mapGetters } from 'vuex';
import ChangelogEntry from './ChangelogEntry.vue';
import {
  CHANGELOG_GROUPS,
  formatChangeValue,
  formatDateTime,
} from './changelog';

const TIMELINE_COLORS = {
  created: 'green',
  initial: 'gray',
  restored: 'orange',
  updated: 'blue',
};

export default {
  name: 'TaskHistory',
  components: { ChangelogEntry, ReloadOutlined },

  data() {
    return {
      group: 'all',
      groups: CHANGELOG_GROUPS,
    };
  },

  computed: {
    ...mapGetters([
      'taskChangelog',
      'taskChangelogCursor',
      'isFetchingTaskHistory',
      'selectedTask',
    ]),

    visibleEntries() {
      if (this.group === 'all') return this.taskChangelog;
      return this.taskChangelog.filter((entry) =>
        entry.changes.some((c) => c.group === this.group)
      );
    },

    // The newest entry is the task's current state, so it can't be restored to
    latestHistoryId() {
      return this.taskChangelog[0]?.history_id;
    },
  },

  mounted() {
    if (this.selectedTask)
      this.fetchTaskChangelog({ taskId: this.selectedTask, reset: true });
  },

  methods: {
    ...mapActions([
      'fetchTaskChangelog',
      'previewTaskRevert',
      'revertTaskToHistory',
    ]),

    refresh() {
      if (!this.selectedTask) return;
      this.fetchTaskChangelog({ taskId: this.selectedTask });
    },

    loadMore() {
      this.fetchTaskChangelog({ taskId: this.selectedTask, loadMore: true });
    },

    timelineColor(type) {
      return TIMELINE_COLORS[type] || 'blue';
    },

    async confirmRestore(entry) {
      const changes = await this.previewTaskRevert({
        taskId: this.selectedTask,
        historyId: entry.history_id,
      });
      if (changes === null) return;

      const versionDate = formatDateTime(entry.history_date);
      if (!changes.length) {
        Modal.info({
          title: 'Nothing to restore',
          content: `The operation already matches the version from ${versionDate}.`,
        });
        return;
      }

      Modal.confirm({
        title: `Restore the version from ${versionDate}?`,
        width: 560,
        okText: 'Restore',
        content: h('div', [
          h('p', 'The following settings will change:'),
          h(
            'ul',
            {
              style: 'padding-left: 18px; max-height: 300px; overflow-y: auto',
            },
            changes.map((change) =>
              h('li', { key: change.field }, [
                h('strong', `${change.label}: `),
                this.describeRestore(change),
              ])
            )
          ),
        ]),
        onOk: () =>
          this.revertTaskToHistory({
            taskId: this.selectedTask,
            historyId: entry.history_id,
          }),
      });
    },

    // Dry-run changes are phrased current (old) → restored (new)
    describeRestore(change) {
      if (change.kind === 'steps' || change.kind === 'step_list') {
        const parts = [];
        if (change.added?.length) parts.push(`adds ${change.added.join(', ')}`);
        if (change.removed?.length)
          parts.push(`removes ${change.removed.join(', ')}`);
        if (change.reordered) parts.push('restores the step order');
        if (change.regrouped) parts.push('restores the step groups');
        return parts.join('; ') || 'restored';
      }
      if (change.kind === 'json' || change.kind === 'long_text')
        return 'restored to the earlier value';
      return `${formatChangeValue(change, change.old)} → ${formatChangeValue(
        change,
        change.new
      )}`;
    },
  },
};
</script>

<style scoped>
.changelog-container {
  padding: 8px 4px;
  max-height: 70vh;
  overflow-y: auto;
}

.changelog-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.changelog-timeline {
  padding-top: 4px;
}
</style>
